/**
 * Genel Bakış (Dashboard) — v5
 *
 * Veri kaynakları:
 *   · Lisans          → mock
 *   · Embed / Meta    → GET /api/install/status
 *   · Katalog Sağlığı → useSizing() + GET /api/products?statusFilter=passive (pasif adedi)
 *
 * Değişiklikler v5:
 *   · License: Paket | Başlangıç | Bitiş aynı satırda 3 kolon
 *   · Embed + Meta: üstlü altlı değil, yan yana 2 kolon (sağ %50 içinde)
 *   · Ürünler section: 6 chip → aktif, pasif, muaf, tablosuz, modeli yok, görseli yok
 *   · info severity (nötr gri chip)
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LayoutDashboard, ArrowRight,
    Package, Table2, Ruler, Grid3x3, Shirt, UserRound, Image as ImageIcon,
    Clock, Receipt, Code2, Tag, Loader2, RefreshCw,
} from "lucide-react";

import { useI18n } from "@/lib/i18n";
import { useSizing } from "@/lib/sizing-store";
import { api } from "@/lib/api";
import { useLicense } from "@/lib/license";
import { PageHeader, Card, CardHeader, Eyebrow } from "@/components/design-system";

// ─── Lisans verisi düzgünü ─────────────────────────────────────────────
// Backend /license cevabı plan/trial/quota alanlarını ham tutuyor; kart
// bunları insan-dostu şekle çevirir. Lisans henüz yüklenmediyse null döner
// ve kart iskelet gösterir.
function shapeLicenseForCard(license) {
    if (!license || typeof license !== "object") return null;
    const now = Date.now();
    const parseIso = (v) => (v ? new Date(v).getTime() : null);
    const isTrial = (license.status || "trial") === "trial";

    const trialStart = parseIso(license.trial_started_at);
    const trialDays = Number(license.trial_days) || 14;
    const trialEnd = trialStart ? trialStart + trialDays * 86400000 : null;
    const nextBilling = parseIso(license.next_billing_at);
    const activatedAt = parseIso(license.activated_at);

    const startedAtMs = activatedAt || trialStart || now;
    const expiresAtMs = isTrial ? (trialEnd || now) : (nextBilling || now);
    const daysRemaining = Math.max(0, Math.ceil((expiresAtMs - now) / 86400000));

    return {
        plan: "Sayz",
        trial: isTrial,
        trialDaysLeft: isTrial ? daysRemaining : null,
        startedAt: new Date(startedAtMs).toISOString(),
        expiresAt: new Date(expiresAtMs).toISOString(),
        daysRemaining,
        autoRenew: !isTrial && (license.billing_cycle === "monthly" || license.billing_cycle === "yearly"),
        quota: {
            used: Number(license.usage_this_month) || 0,
            limit: Number(license.quota_this_month) || 200_000,
            label: "widget çağrısı",
        },
    };
}

// ─── MAIN ──────────────────────────────────────────────────────────────────
export default function Dashboard() {
    const { t } = useI18n();
    const sizing = useSizing();
    const { license } = useLicense();
    const [install, setInstall] = useState(null);
    const [installErr, setInstallErr] = useState(false);
    const [passiveCount, setPassiveCount] = useState(null);

    useEffect(() => {
        api.get("/install/status")
            .then((r) => setInstall(r.data))
            .catch(() => setInstallErr(true));
        api.get("/products?statusFilter=passive&limit=1")
            .then((r) => setPassiveCount(r.data?.total ?? 0))
            .catch(() => setPassiveCount(0));
    }, []);

    const health = useMemo(
        () => computeHealth(sizing, passiveCount),
        [sizing, passiveCount]
    );

    // License provider: `null` = loading, `false` = no session, obj = data.
    // Card shows skeleton until obj arrives (never flashes mock defaults).
    const licenseView = useMemo(
        () => (license && typeof license === "object" ? shapeLicenseForCard(license) : null),
        [license]
    );

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <PageHeader
                icon={LayoutDashboard}
                title={t("dash.title")}
                description="Hesap durumun ve kataloğunun tek bakışta özeti."
            />

            {/* 1 · Hesap & Lisans (sol %50) + Bağlantı Durumları (sağ %50, yan yana) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <LicenseCard data={licenseView} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <ConnectionCard
                        icon={Code2}
                        title="Head Embed Script"
                        desc="Bağlantı durumu."
                        state={deriveConnection(install?.head, installErr)}
                        testKind="head"
                        onRefresh={(data) => setInstall(data)}
                        okMessage="Script bulundu."
                        testId="dashboard-embed-status"
                    />
                    <ConnectionCard
                        icon={Tag}
                        title="Ürün Detay SKU Etiketi"
                        desc="Bağlantı durumu."
                        state={deriveConnection(install?.meta, installErr)}
                        testKind="meta"
                        onRefresh={(data) => setInstall(data)}
                        okMessage="SKU etiketi tüm URL'lerde bulundu."
                        testId="dashboard-meta-status"
                    />
                </div>
            </div>

            {/* 2 · Katalog Sağlığı başlık */}
            <CatalogHealthHeader health={health} loaded={sizing.loaded} />

            {/* 3 · PRIMARY — Ürünler + Beden Tabloları */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <PrimarySection
                    testKey="products"
                    icon={Package}
                    title="Ürünler"
                    desc="Katalogdaki tüm ürünler, beden ve model eksikleri."
                    href="/app/catalog/products"
                    total={health.products.total}
                    unit="toplam ürün"
                    issues={health.products.issues}
                    meta={health.products.meta}
                    loaded={sizing.loaded}
                />
                <PrimarySection
                    testKey="tables"
                    icon={Table2}
                    title="Beden Tabloları"
                    desc="Oluşturduğun beden tablolar ve ürün atamaları."
                    href="/app/catalog/tables"
                    total={health.tables.total}
                    unit="toplam tablo"
                    issues={health.tables.issues}
                    meta={health.tables.meta}
                    loaded={sizing.loaded}
                />
            </div>

            {/* 4 · SECONDARY — kompakt tile'lar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                <SecondaryTile icon={Ruler}       title="Beden Serileri"   total={health.series.total}  unit="seri"   href="/app/sizing/sizes"              issues={health.series.issues}  loaded={sizing.loaded} testKey="series"  />
                <SecondaryTile icon={Grid3x3}     title="Ölçü Grupları"    total={health.groups.total}  unit="grup"   href="/app/sizing/measurement-groups" issues={health.groups.issues}  loaded={sizing.loaded} testKey="groups"  />
                <SecondaryTile icon={Shirt}       title="Kalıp Bilgileri"  total={health.fit.total}     unit="kalıp"  href="/app/sizing/fit-info"            issues={health.fit.issues}     loaded={sizing.loaded} testKey="fit"     />
                <SecondaryTile icon={UserRound}   title="Modeller"          total={health.models.total}  unit="model"  href="/app/sizing/models"              issues={health.models.issues}  loaded={sizing.loaded} testKey="models"  />
                <SecondaryTile icon={ImageIcon}   title="Görseller"         total={health.images.total}  unit="görsel" href="/app/sizing/images"              issues={health.images.issues}  loaded={sizing.loaded} testKey="images"  />
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Data derivation
// ═══════════════════════════════════════════════════════════════════════════

function computeHealth(sizing, passiveCount) {
    const products       = sizing.products || [];
    const activeTotal    = sizing.productsTotal || products.length;
    const activeProducts = products.filter((p) => !p.sizingTableExempt);
    const tables         = sizing.tables || [];
    const series         = sizing.series || [];
    const groups         = sizing.groups || [];
    const models         = sizing.models || [];
    const images         = sizing.images || [];

    const noTable  = activeProducts.filter((p) => !p.tableId).length;
    const noModel  = activeProducts.filter((p) => !p.modelId).length;
    const noImage  = activeProducts.filter((p) => !p.sizingImageId).length;
    const exempt   = products.filter((p) => p.sizingTableExempt).length;
    const passive  = passiveCount == null ? null : passiveCount;
    const tablesUnassigned = tables.filter((t) => !products.some((p) => p.tableId === t.id)).length;
    const seriesUnused = series.filter((s) =>
        !tables.some((t) => t.sourceSeriesId === s.id || (!t.sourceSeriesId && t.seriesLabel === s.name))
    ).length;
    const groupsUnused = groups.filter((g) =>
        !tables.some((t) => t.groupLabel === g.name || t.sourceGroupId === g.id)
    ).length;
    const productsNoFit = activeProducts.filter((p) => !p.fit).length;
    const imagesUnused = images.filter((img) => !products.some((p) => p.sizingImageId === img.id)).length;

    const totalWithPassive = (passive != null) ? (activeTotal + passive) : activeTotal;

    return {
        products: {
            total: totalWithPassive,
            issues: sortBySeverity(compact([
                noTable > 0 && { label: "tablosuz",           count: noTable,  severity: "critical", href: "/app/catalog/products?tablePresence=none" },
                noModel > 0 && { label: "modeli yok",         count: noModel,  severity: "warning",  href: "/app/catalog/products?modelPresence=none" },
                noImage > 0 && { label: "görseli yok",        count: noImage,  severity: "warning",  href: "/app/catalog/products?imagePresence=none" },
                passive != null && passive > 0 && { label: "pasif", count: passive, severity: "info", href: "/app/catalog/products?statusFilter=passive" },
                exempt > 0 && { label: "muaf",                count: exempt,   severity: "info",     href: "/app/catalog/products?tablePresence=exempt" },
            ])),
            meta: null,
        },
        tables: {
            total: tables.length,
            issues: compact([
                tablesUnassigned > 0 && { label: "tanesi ürünlere atanmamış", count: tablesUnassigned, severity: "critical", href: "/app/catalog/tables?assignment=unassigned" },
            ]),
            meta: null,
        },
        series: {
            total: series.length,
            issues: compact([
                seriesUnused > 0 && { label: "kullanılmıyor", count: seriesUnused, severity: "warning", href: "/app/sizing/sizes" },
            ]),
        },
        groups: {
            total: groups.length,
            issues: compact([
                groupsUnused > 0 && { label: "kullanılmıyor", count: groupsUnused, severity: "warning", href: "/app/sizing/measurement-groups" },
            ]),
        },
        models: { total: models.length, issues: [] },
        fit: {
            total: 3,
            issues: compact([
                productsNoFit > 0 && { label: "atanmamış ürün", count: productsNoFit, severity: "warning", href: "/app/catalog/products?fitPresence=none" },
            ]),
        },
        images: {
            total: images.length,
            issues: compact([
                imagesUnused > 0 && { label: "kullanılmıyor", count: imagesUnused, severity: "warning", href: "/app/sizing/images" },
            ]),
        },
    };
}

function compact(arr) { return arr.filter(Boolean); }

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };
function sortBySeverity(arr) {
    return [...arr].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99));
}

function deriveConnection(block, errored) {
    if (errored) return { status: "unknown", label: "Yüklenemedi", lastCheck: null, detail: "Durum alınamadı" };
    if (!block)  return { status: "loading", label: "Yükleniyor…", lastCheck: null, detail: null };

    const statusKey = block.status || "unknown";
    const map = {
        connected:    { status: "connected",    label: "Bağlı" },
        disconnected: { status: "disconnected", label: "Bağlı Değil" },
        unknown:      { status: "unknown",      label: "Henüz Test Edilmedi" },
    };
    const m = map[statusKey] || map.unknown;
    return { ...m, lastCheck: block.last_at || null, detail: block.reason || null };
}

// ═══════════════════════════════════════════════════════════════════════════
// Shared primitives
// ═══════════════════════════════════════════════════════════════════════════

const StatusDot = ({ status }) => {
    const bg = status === "critical" ? "bg-destructive"
        : status === "warning" ? "bg-amber-500"
        : "bg-emerald-500";
    return <span className={`inline-block h-1.5 w-1.5 rounded-full ${bg} shrink-0`} aria-label={status} />;
};

/** Header dot: info chip'ler sağlığı etkilemez — yalnızca warning + critical bakılır. */
const worstOf = (issues = []) =>
    issues.reduce((a, i) => {
        if (i.severity === "critical") return "critical";
        if (i.severity === "warning")  return a === "critical" ? "critical" : "warning";
        return a;
    }, "ok");

const CHIP_STYLES = {
    critical: {
        bg: "bg-destructive/[0.08] hover:bg-destructive/[0.14] text-destructive",
        dot: "bg-destructive",
    },
    warning: {
        bg: "bg-amber-500/[0.10] hover:bg-amber-500/[0.18] text-amber-700 dark:text-amber-400",
        dot: "bg-amber-500",
    },
    info: {
        bg: "bg-foreground/[0.05] hover:bg-foreground/[0.09] text-muted-foreground hover:text-foreground",
        dot: "bg-muted-foreground/60",
    },
};

const IssueChip = ({ count, label, severity, href, size = "md" }) => {
    const s = CHIP_STYLES[severity] || CHIP_STYLES.info;
    const sizeCls = size === "sm"
        ? "gap-1 px-1.5 py-0.5 text-[10.5px]"
        : "gap-1.5 px-2 py-1 text-[11.5px]";

    return (
        <Link
            to={href}
            className={`group inline-flex items-center rounded-md font-medium transition-colors ${s.bg} ${sizeCls}`}
            data-testid={`issue-chip-${label.replace(/\s+/g, "-")}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            <strong className="font-semibold tabular-nums">{count.toLocaleString("tr-TR")}</strong>
            <span className="opacity-85">{label}</span>
        </Link>
    );
};

/**
 * HeaderWithIcon — ikon chip + sağında başlık/açıklama stack.
 * Açıklama: text-xs text-muted-foreground.
 */
const HeaderWithIcon = ({ icon: Icon, title, desc, trailing = null }) => (
    <span className="inline-flex items-start gap-2.5">
        <span className="h-9 w-9 rounded-lg border border-border bg-muted/40 grid place-items-center shrink-0 mt-0.5">
            <Icon className="h-4 w-4 text-foreground/80" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2 min-w-0">
                <span className="font-display text-[15px] font-semibold tracking-tight truncate">{title}</span>
                {trailing}
            </span>
            {desc && (
                <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                    {desc}
                </span>
            )}
        </span>
    </span>
);

// ═══════════════════════════════════════════════════════════════════════════
// 1 · HESAP & LİSANS — Paket / Başlangıç / Bitiş yan yana (3 kolon)
// ═══════════════════════════════════════════════════════════════════════════
const LicenseCard = ({ data }) => {
    // Loading state — license henüz /license çağrısından dönmediyse iskelet
    // kart gösterir. Mock değer flash etmez (önceki version mockLicense'ı
    // ilk render'da gösteriyordu, kullanıcı 'Growth' plan yanılsamasında
    // kalıyordu).
    if (!data) {
        return (
            <Card className="overflow-hidden" data-testid="dashboard-license-section">
                <CardHeader
                    title={
                        <HeaderWithIcon
                            icon={Receipt}
                            title="Hesap & Lisans"
                            desc="Paket durumu, yenileme tarihi ve aylık widget çağrı kotası."
                        />
                    }
                />
                <div className="p-5 space-y-5" data-testid="dashboard-license-loading">
                    <div className="grid grid-cols-3 gap-5">
                        <div className="h-10 rounded-md bg-muted/40 animate-pulse" />
                        <div className="h-10 rounded-md bg-muted/40 animate-pulse" />
                        <div className="h-10 rounded-md bg-muted/40 animate-pulse" />
                    </div>
                    <div className="pt-4 border-t border-border/60">
                        <div className="h-4 w-32 rounded bg-muted/40 animate-pulse mb-3" />
                        <div className="h-1 w-full rounded-full bg-muted/40 animate-pulse" />
                    </div>
                </div>
            </Card>
        );
    }

    const quotaPct = Math.round((data.quota.used / data.quota.limit) * 100);
    const quotaCritical = quotaPct >= 90;
    const quotaWarning = quotaPct >= 70 && !quotaCritical;
    const dateFmt = (iso) => new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });

    const quotaBar = quotaCritical ? "bg-destructive"
        : quotaWarning ? "bg-amber-500"
        : "bg-emerald-500";
    const quotaChip = quotaCritical
        ? "bg-destructive/[0.08] text-destructive"
        : quotaWarning
        ? "bg-amber-500/[0.10] text-amber-700 dark:text-amber-400"
        : "bg-emerald-500/[0.10] text-emerald-700 dark:text-emerald-400";

    const planChip = data.trial
        ? "bg-amber-500/[0.10] text-amber-700 dark:text-amber-400"
        : "bg-emerald-500/[0.10] text-emerald-700 dark:text-emerald-400";
    const planLabel = data.trial ? `Deneme · ${data.trialDaysLeft}g kaldı` : "Aktif";

    return (
        <Card className="overflow-hidden" data-testid="dashboard-license-section">
            <CardHeader
                title={
                    <HeaderWithIcon
                        icon={Receipt}
                        title="Hesap & Lisans"
                        desc="Paket durumu, yenileme tarihi ve aylık widget çağrı kotası."
                    />
                }
            />
            <div className="p-5 space-y-5">
                {/* Paket / Başlangıç / Bitiş — 3 kolon yan yana */}
                <div className="grid grid-cols-3 gap-5">
                    {/* Paket */}
                    <div>
                        <Eyebrow>Paket</Eyebrow>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="font-display text-[18px] font-semibold tracking-tight leading-none">
                                {data.plan}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold tracking-wide ${planChip}`}>
                                {planLabel}
                            </span>
                        </div>
                    </div>
                    {/* Başlangıç */}
                    <div>
                        <div className="flex items-center gap-1 mb-1">
                            <Clock className="h-3 w-3 text-foreground/50" strokeWidth={1.75} />
                            <Eyebrow>Başlangıç</Eyebrow>
                        </div>
                        <div className="font-display text-[18px] font-semibold tracking-tight leading-none">
                            {dateFmt(data.startedAt)}
                        </div>
                    </div>
                    {/* Bitiş */}
                    <div>
                        <div className="flex items-center gap-1 mb-1">
                            <Clock className="h-3 w-3 text-foreground/50" strokeWidth={1.75} />
                            <Eyebrow>Bitiş · {data.daysRemaining}g kaldı</Eyebrow>
                        </div>
                        <div className="font-display text-[18px] font-semibold tracking-tight leading-none">
                            {dateFmt(data.expiresAt)}
                        </div>
                    </div>
                </div>

                {/* Quota — full width, Analitik > Trafik tab'ına link */}
                <Link
                    to="/app/analytics?tab=traffic"
                    className="block pt-4 border-t border-border/60 group"
                    data-testid="dashboard-quota-link"
                >
                    <div className="flex items-center justify-between mb-2">
                        <Eyebrow>Aylık {data.quota.label}</Eyebrow>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold tabular-nums ${quotaChip}`}>
                            {data.quota.used.toLocaleString("tr-TR")} / {data.quota.limit.toLocaleString("tr-TR")}
                        </span>
                    </div>
                    <div className="relative h-1 w-full rounded-full bg-foreground/[0.06] overflow-hidden">
                        <div
                            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${quotaBar}`}
                            style={{ width: `${Math.min(100, quotaPct)}%` }}
                            data-testid="dashboard-quota-bar"
                        />
                    </div>
                    <div className="mt-1.5 text-[11px] text-muted-foreground group-hover:text-foreground transition-colors">
                        %{quotaPct} kullanıldı · Trafik detayına git <ArrowRight className="inline h-2.5 w-2.5 ml-0.5" />
                    </div>
                </Link>
            </div>
        </Card>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Bağlantı Durumu Kartı — Embed / Meta
// ═══════════════════════════════════════════════════════════════════════════
const CONNECTION_META = {
    connected:    { chip: "bg-emerald-500/[0.10] text-emerald-700 dark:text-emerald-400", dot: "bg-emerald-500" },
    disconnected: { chip: "bg-destructive/[0.08] text-destructive",                       dot: "bg-destructive" },
    unknown:      { chip: "bg-foreground/[0.06] text-muted-foreground",                   dot: "bg-muted-foreground/60" },
    loading:      { chip: "bg-foreground/[0.06] text-muted-foreground",                   dot: "bg-muted-foreground/40" },
};

const ConnectionCard = ({ icon, title, desc, state, testKind, onRefresh, okMessage, testId }) => {
    const m = CONNECTION_META[state.status] || CONNECTION_META.unknown;
    const [testing, setTesting] = useState(false);

    const runTest = async () => {
        if (testing) return;
        setTesting(true);
        try {
            const r = await api.post(`/install/test-${testKind}`);
            onRefresh?.(r.data);
        } catch {
            // ignore – state will stay as-is; next poll will refresh
        } finally {
            setTesting(false);
        }
    };

    // Content line: ok → ok mesajı; disconnected → backend reason; diğer → '—'
    const infoLine =
        state.status === "connected" ? okMessage
        : state.status === "disconnected" ? (state.detail || "Bağlantı doğrulanamadı.")
        : state.status === "loading" ? "Durum yükleniyor…"
        : "Henüz test edilmedi.";

    return (
        <Card className="overflow-hidden flex flex-col" data-testid={testId}>
            <CardHeader
                title={<HeaderWithIcon icon={icon} title={title} desc={desc} />}
            />
            <div className="p-5 flex-1 flex flex-col">
                <div className="space-y-1.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-semibold ${m.chip}`}>
                        {state.status === "loading"
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />}
                        {state.label}
                    </span>
                    <div className="text-xs text-muted-foreground">
                        Son Kontrol · <strong className="text-foreground/80 font-semibold">{fmtAgo(state.lastCheck)}</strong>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {infoLine}
                    </div>
                </div>

                <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={runTest}
                        disabled={testing}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold border border-border bg-background hover:bg-foreground/[0.04] text-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`${testId}-test-button`}
                    >
                        {testing
                            ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                            : <RefreshCw className="h-3 w-3" strokeWidth={2} />}
                        {testing ? "Test ediliyor…" : "Bağlantıyı Test Et"}
                    </button>
                </div>
            </div>
        </Card>
    );
};

function fmtAgo(iso) {
    if (!iso) return "hiç";
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const secs = Math.max(1, Math.floor((now - then) / 1000));
        if (secs < 60) return `${secs} sn önce`;
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `${mins} dk önce`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} sa önce`;
        const days = Math.floor(hrs / 24);
        return `${days} gün önce`;
    } catch { return "—"; }
}

// ═══════════════════════════════════════════════════════════════════════════
// Katalog Sağlığı başlık
// ═══════════════════════════════════════════════════════════════════════════
const CatalogHealthHeader = ({ health, loaded }) => {
    const totals = useMemo(() => {
        if (!loaded) return { crit: 0, warn: 0 };
        const all = [
            health.products, health.tables, health.series, health.groups,
            health.models, health.fit, health.images,
        ];
        let crit = 0, warn = 0;
        all.forEach(sec => (sec.issues || []).forEach(i => {
            if (i.severity === "critical") crit++;
            else if (i.severity === "warning") warn++;
        }));
        return { crit, warn };
    }, [health, loaded]);

    return (
        <div className="flex items-end justify-between gap-3 flex-wrap pt-2" data-testid="catalog-health-header">
            <div>
                <Eyebrow>Katalog Sağlığı</Eyebrow>
                <div className="mt-1 text-xs font-normal text-muted-foreground max-w-xl">
                    Her bölüm → tıklanabilir issue chip'leri ile doğrudan düzeltme sayfasına götürür.
                </div>
            </div>
            {!loaded ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-foreground/[0.06] text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Yükleniyor…
                </span>
            ) : (
                <div className="flex items-center gap-1.5">
                    {totals.crit > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-destructive/[0.08] text-destructive">
                            <StatusDot status="critical" />
                            {totals.crit} kritik
                        </span>
                    )}
                    {totals.warn > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-500/[0.10] text-amber-700 dark:text-amber-400">
                            <StatusDot status="warning" />
                            {totals.warn} uyarı
                        </span>
                    )}
                    {totals.crit === 0 && totals.warn === 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-500/[0.10] text-emerald-700 dark:text-emerald-400">
                            <StatusDot status="ok" />
                            Her şey yerinde
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// PRIMARY SECTION
// ═══════════════════════════════════════════════════════════════════════════
const PrimarySection = ({ testKey, icon, title, desc, href, total, unit, issues, meta, loaded }) => {
    const status = worstOf(issues);

    return (
        <Card className="overflow-hidden" data-testid={`primary-section-${testKey}`}>
            <CardHeader
                title={
                    <HeaderWithIcon
                        icon={icon}
                        title={title}
                        desc={desc}
                        trailing={loaded ? <StatusDot status={status} /> : null}
                    />
                }
                right={
                    <Link
                        to={href}
                        className="group inline-flex items-center gap-1 text-[11.5px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                        data-testid={`primary-section-${testKey}-manage`}
                    >
                        Yönet
                        <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                }
            />
            <div className="p-5">
                {/* Total */}
                <div className="flex items-baseline gap-2">
                    {loaded ? (
                        <>
                            <div className="font-display text-[42px] font-semibold tracking-tight leading-none tabular-nums text-foreground">
                                {total.toLocaleString("tr-TR")}
                            </div>
                            <div className="text-xs text-muted-foreground">{unit}</div>
                        </>
                    ) : (
                        <div className="h-10 w-24 rounded bg-foreground/[0.06] animate-pulse" />
                    )}
                </div>

                {/* Chips */}
                {loaded && (
                    issues.length > 0 ? (
                        <div className="mt-5 pt-4 border-t border-border/60 flex items-center gap-2 flex-wrap">
                            {issues.map((iss, i) => (
                                <IssueChip key={i} count={iss.count} label={iss.label} severity={iss.severity} href={iss.href} />
                            ))}
                        </div>
                    ) : (
                        <div className="mt-5 pt-4 border-t border-border/60 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11.5px] font-medium bg-emerald-500/[0.10] text-emerald-700 dark:text-emerald-400">
                                <StatusDot status="ok" />
                                Her şey yerinde
                            </span>
                        </div>
                    )
                )}

                {meta && <div className="mt-3 text-xs text-muted-foreground">{meta}</div>}
            </div>
        </Card>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// SECONDARY TILE
// ═══════════════════════════════════════════════════════════════════════════
const SecondaryTile = ({ icon: Icon, title, total, unit, href, issues = [], loaded, testKey }) => {
    const status = worstOf(issues);
    return (
        <Card className="overflow-hidden group" data-testid={`secondary-tile-${testKey}`}>
            <div className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span className="h-9 w-9 rounded-lg border border-border bg-muted/40 grid place-items-center shrink-0">
                            <Icon className="h-4 w-4 text-foreground/75" strokeWidth={1.75} />
                        </span>
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="font-display text-[13.5px] font-semibold tracking-tight truncate">
                                    {title}
                                </span>
                                {loaded && <StatusDot status={status} />}
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                                {loaded
                                    ? <><strong className="text-foreground/80 font-semibold">{total.toLocaleString("tr-TR")}</strong> {unit}</>
                                    : <span className="inline-block h-3 w-16 rounded bg-foreground/[0.06] animate-pulse align-middle" />}
                            </div>
                        </div>
                    </div>
                    <Link
                        to={href}
                        aria-label={`${title} sayfasına git`}
                        className="inline-grid place-items-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04] transition-colors shrink-0"
                        data-testid={`secondary-tile-${testKey}-arrow`}
                    >
                        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>

                {loaded && issues.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-1.5 flex-wrap">
                        {issues.map((iss, i) => (
                            <IssueChip key={i} count={iss.count} label={iss.label} severity={iss.severity} href={iss.href} size="sm" />
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};
